'use client';

import { Loader2 } from 'lucide-react';
import { useFormatter, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { priceTargetCents, useDeletePriceAlert } from '@/lib/api/priceAlerts';
import { Link } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { formatCents } from '@/lib/utils/formatAmount';
import type { PriceAlert } from '../types';
import {
  conditionSymbol,
  formatUsdCompact,
  isOpenInterestCondition,
  isPriceCondition,
  isWhaleCondition,
} from '../utils';

interface PriceAlertCardProps {
  alert: PriceAlert;
  onEdit: () => void;
}

export function PriceAlertCard({ alert, onEdit }: PriceAlertCardProps) {
  const t = useTranslations('priceAlerts');
  const tColumns = useTranslations('priceAlerts.columns');
  const tCommon = useTranslations('common');
  const format = useFormatter();
  const deleteMutation = useDeletePriceAlert();
  const isDeleting = deleteMutation.isPending;

  const handleDelete = () => {
    deleteMutation.mutate(alert.id);
  };

  const isYes = alert.side === 'YES';
  const symbol = conditionSymbol(alert.conditionType);
  const isPrice = isPriceCondition(alert.conditionType);
  const usdValue = formatUsdCompact(alert.targetValue);

  const getTargetText = () => {
    if (isPrice) {
      return t('row.priceTarget', {
        symbol,
        value: formatCents(priceTargetCents(alert.targetValue)),
      });
    }
    if (isWhaleCondition(alert.conditionType)) {
      return t('row.whaleTarget', { value: usdValue });
    }
    if (isOpenInterestCondition(alert.conditionType)) {
      return t('row.oiTarget', { symbol, value: usdValue });
    }
    return t('row.volumeTarget', { symbol, value: usdValue });
  };
  const targetText = getTargetText();

  const getCurrentText = () => {
    if (alert.currentValue === null || alert.currentValue === undefined) return null;
    if (isPrice) {
      return t('row.now', { value: formatCents(priceTargetCents(alert.currentValue)) });
    }
    return t('row.now', { value: formatUsdCompact(alert.currentValue) });
  };
  const currentText = getCurrentText();

  const lastSentText = alert.lastSentAt
    ? format.dateTime(new Date(alert.lastSentAt), {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
      })
    : t('lastSent.never');

  const statusColor = alert.status === 'ACTIVE' ? 'text-v2-green' : 'text-v2-text-secondary';
  const STATUS_LABEL_KEYS = {
    ACTIVE: 'status.active',
    TRIGGERED: 'status.triggered',
    CANCELLED: 'status.cancelled',
    EXPIRED: 'status.expired',
  } as const;
  const statusLabel = t(STATUS_LABEL_KEYS[alert.status]);

  const typeLabel = alert.repeatMode === 'ONCE' ? t('type.once') : t('type.everyTrigger');

  const normalizedChannel = alert.lastSentChannel?.toUpperCase();
  const lastSentChannelLabel =
    normalizedChannel === 'TELEGRAM' || normalizedChannel === 'PUSH'
      ? t(`channels.${normalizedChannel}`)
      : alert.lastSentChannel;

  return (
    <div className="flex flex-col gap-4 rounded-[10px] bg-v2-btn-secondary p-4">
      {/* Header: outcome name + side badge + event link */}
      <div className="flex flex-col gap-2">
        <span className="text-[12px] leading-4 font-normal text-v2-text-primary">
          {alert.outcomeName?.trim() || alert.note}
        </span>
        <div className="flex min-w-0 items-center gap-1">
          <span
            className={cn(
              'shrink-0 rounded-[8px] px-2 py-0.5 text-[12px] leading-4 font-normal',
              isYes ? 'bg-v2-green-surface text-v2-green' : 'bg-v2-red-surface text-red-400',
            )}
          >
            {isYes ? t('side.yes') : t('side.no')}
          </span>
          {alert.eventSlug ? (
            <Link
              href={`/event/${alert.eventSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-[13px] leading-4 font-normal text-v2-text-secondary transition-colors hover:text-v2-text-primary hover:underline"
            >
              {alert.eventName}
            </Link>
          ) : (
            <span className="truncate text-[13px] leading-4 font-normal text-v2-text-secondary">
              {alert.eventName}
            </span>
          )}
        </div>
      </div>

      {/* Two rows of two cells */}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <CardField label={tColumns('target')}>
            <span>{targetText}</span>
            {currentText && <span className="text-v2-text-secondary">({currentText})</span>}
          </CardField>
          <CardField label={tColumns('lastSent')}>
            <span>{lastSentText}</span>
            {lastSentChannelLabel && (
              <span className="text-v2-text-secondary">{lastSentChannelLabel}</span>
            )}
          </CardField>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <CardField label={tColumns('type')}>{typeLabel}</CardField>
          <CardField label={tColumns('status')} valueClassName={statusColor}>
            {statusLabel}
          </CardField>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          appearance="outline"
          size="S"
          className="flex-1"
          onClick={onEdit}
          aria-label={t('row.editAria')}
          leftIcon={<Icon name="Edit" size={16} />}
        >
          {tCommon('edit')}
        </Button>
        <Button
          type="button"
          appearance="outline"
          size="S"
          className="flex-1"
          onClick={handleDelete}
          disabled={isDeleting}
          aria-label={t('row.deleteAria')}
          aria-busy={isDeleting || undefined}
          leftIcon={isDeleting ? <Loader2 className="size-4 animate-spin" /> : undefined}
        >
          {tCommon('delete')}
        </Button>
      </div>
    </div>
  );
}

interface CardFieldProps {
  label: string;
  valueClassName?: string;
  children: ReactNode;
}

function CardField({ label, valueClassName, children }: CardFieldProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] leading-none font-normal text-v2-text-secondary">{label}</span>
      <div
        className={cn(
          'flex flex-col text-[12px] leading-[150%] font-normal text-v2-text-primary',
          valueClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
